import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  companyNameSchema,
  companyRoleSchema,
  passwordSchema,
  repeatPasswordSchema,
} from '../helpers/validations';
import { signUp, RestError } from '../rest/authClient';
import SignUpTO from '../model/SignUpTO';

import styles from '../styles/pages/SignUp.module.css';
import base from '../styles/Base.module.css';
import DefaultPage from './DefaultPage';
import { useUserUpdateContext } from '../contexts/UserContext';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import { ActionButton } from '@ax2/common/components/ActionButton';
import { getErrorMessage } from '@ax2/common/helpers/error';
import * as Yup from 'yup';

const signUpSchema = Yup.object().shape({
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  companyName: companyNameSchema,
  companyRole: companyRoleSchema,
  password: passwordSchema,
  password2: repeatPasswordSchema,
});

const SignUp: React.FC<RouteComponentProps> = ({ history }) => {
  const updateError = useErrorUpdateContext();
  const updateUser = useUserUpdateContext();
  return (
    <DefaultPage header={{ title: 'Sign Up' }}>
      <Formik
        initialValues={{
          email: '',
          firstName: '',
          lastName: '',
          companyName: '',
          companyRole: '',
          password: '',
          password2: '',
        }}
        validationSchema={signUpSchema}
        onSubmit={(values, { setSubmitting }) => {
          signUp(
            new SignUpTO(
              values.email,
              values.password,
              values.firstName.trim(),
              values.lastName.trim(),
              values.companyName,
              values.companyRole
            )
          )
            .then(authResponse => {
              setSubmitting(false);
              updateUser(authResponse);
              history.push('/dashboard');
            })
            .catch((err: RestError) => {
              setSubmitting(false);
              updateError({
                text: getErrorMessage(err),
              });
            });
        }}
      >
        {({ isSubmitting, handleSubmit }) => (
          <Form className={styles.userForm}>
            <div className={styles.formBox}>
              <p className={base.sectionText}>Account</p>
              <label className={styles.userFormLabel}>Company Email</label>
              <Field
                autoComplete="new-password"
                type="email"
                name="email"
                className={styles.userInput}
              />
              <ErrorMessage
                name="email"
                component="div"
                className={styles.userFormError}
              />
              <div className={styles.stackItems}>
                <div className={styles.verticalItems}>
                  <label className={styles.userFormLabel}>First Name</label>
                  <Field
                    type="text"
                    name="firstName"
                    className={styles.userInputStackItem}
                  />
                  <ErrorMessage
                    name="firstName"
                    component="div"
                    className={styles.userErrorStackItem}
                  />
                </div>
                <div className={styles.verticalItems}>
                  <label className={styles.userFormLabel}>Last Name</label>
                  <Field
                    type="text"
                    name="lastName"
                    className={styles.userInputStackItem}
                  />
                  <ErrorMessage
                    name="lastName"
                    component="div"
                    className={styles.userErrorStackItem}
                  />
                </div>
              </div>
            </div>
            <div className={styles.formBox}>
              <p className={base.sectionText}>Company</p>
              <label className={styles.userFormLabel}>Company name</label>
              <Field
                type="text"
                name="companyName"
                className={styles.userInput}
              />
              <ErrorMessage
                name="companyName"
                component="div"
                className={styles.userFormError}
              />
              <label className={styles.userFormLabel}>Company role</label>
              <Field
                type="text"
                name="companyRole"
                className={styles.userInput}
              />
              <ErrorMessage
                name="companyRole"
                component="div"
                className={styles.userFormError}
              />
            </div>
            <div className={styles.formBox}>
              <p className={base.sectionText}>Security</p>
              <div className={styles.stackItems}>
                <div className={styles.verticalItems}>
                  <label className={styles.userFormLabel}>Password</label>
                  <Field
                    autoComplete="new-password"
                    type="password"
                    name="password"
                    className={styles.userInputStackItem}
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className={styles.userErrorStackItem}
                  />
                </div>
                <div className={styles.verticalItems}>
                  <label className={styles.userFormLabel}>
                    Confirm Password
                  </label>
                  <Field
                    type="password"
                    name="password2"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onKeyPress={(e: any) => {
                      e.key === 'Enter' && !isSubmitting && handleSubmit();
                    }}
                    className={styles.userInputStackItem}
                  />
                  <ErrorMessage
                    name="password2"
                    component="div"
                    className={styles.userErrorStackItem}
                  />
                </div>
              </div>
            </div>
            <div className={styles.signUpActions}>
              <ActionButton
                onClick={() => history.push('/signIn')}
                type="secondary"
                text="Sign In"
              />
              <ActionButton
                onClick={() => !isSubmitting && handleSubmit()}
                loading={isSubmitting}
                type="primary"
                text="Create"
              />
            </div>
          </Form>
        )}
      </Formik>
    </DefaultPage>
  );
};

export default withRouter(SignUp);
